import { useMutation, useQuery } from '@apollo/client';
import { Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ADD_MANUAL_VACCINATION_ENTRY_MUTATION,
  VACCINATION_CARD_QUERY,
  VOID_VACCINATION_CARD_ENTRY_MUTATION,
} from './vaccination-card.gql';

import { assertMutationSucceeded, errorMessage } from '@/shared/api/graphql-errors';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/shared/ui/card';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';

interface VaccinationCardEntry {
  id: string;
  product_id: string | null;
  vaccine_name: string;
  applied_date: string;
  next_application_date: string | null;
  lot_number: string;
  weight_grams_snapshot: number | null;
  veterinarian_name_snapshot: string;
  veterinarian_license_snapshot: string | null;
  source: string;
  sale_order_id: string | null;
  notes: string | null;
  voided_at: string | null;
  void_reason: string | null;
}

interface VaccinationCard {
  id: string;
  pet_id: string;
  card_number: string;
  entries: VaccinationCardEntry[];
}

interface VaccinationCardResponse {
  vaccinationCard: VaccinationCard | null;
}

interface Props {
  pet_id: string;
  pet_name: string;
  can_write: boolean;
  is_admin: boolean;
  on_close: () => void;
}

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function generateIdempotencyKey(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `np-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function VaccinationCardDialog({
  pet_id,
  pet_name,
  can_write,
  is_admin,
  on_close,
}: Props): JSX.Element {
  const { t } = useTranslation();
  const { data, loading, refetch } = useQuery<VaccinationCardResponse>(VACCINATION_CARD_QUERY, {
    variables: { pet_id },
    fetchPolicy: 'cache-and-network',
  });
  const [addEntry, { loading: adding }] = useMutation(ADD_MANUAL_VACCINATION_ENTRY_MUTATION);
  const [voidEntry, { loading: voiding }] = useMutation(VOID_VACCINATION_CARD_ENTRY_MUTATION);

  const [error_text, setErrorText] = useState<string | null>(null);
  const [show_add_form, setShowAddForm] = useState(false);
  const [vaccine_name, setVaccineName] = useState('');
  const [applied_date, setAppliedDate] = useState(todayIso());
  const [next_application_date, setNextApplicationDate] = useState('');
  const [lot_number, setLotNumber] = useState('');
  const [weight_kg, setWeightKg] = useState('');
  const [notes, setNotes] = useState('');
  const [voiding_entry, setVoidingEntry] = useState<VaccinationCardEntry | null>(null);
  const [void_reason, setVoidReason] = useState('');

  const card = data?.vaccinationCard ?? null;
  const entries = card?.entries ?? [];

  const resetAddForm = (): void => {
    setVaccineName('');
    setAppliedDate(todayIso());
    setNextApplicationDate('');
    setLotNumber('');
    setWeightKg('');
    setNotes('');
  };

  const onSubmitAddEntry = async (): Promise<void> => {
    setErrorText(null);
    if (!vaccine_name.trim() || !lot_number.trim() || !applied_date) {
      setErrorText(t('vaccination_card.error_fields_required'));
      return;
    }
    const weight_grams =
      weight_kg.trim() && !Number.isNaN(parseFloat(weight_kg))
        ? Math.round(parseFloat(weight_kg) * 1000)
        : undefined;
    try {
      const result = await addEntry({
        variables: {
          input: {
            pet_id,
            vaccine_name: vaccine_name.trim(),
            applied_date,
            next_application_date: next_application_date || undefined,
            lot_number: lot_number.trim(),
            weight_grams,
            notes: notes.trim() || undefined,
          },
        },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        context: { headers: { 'Idempotency-Key': generateIdempotencyKey() } },
      });
      assertMutationSucceeded(result, 'add_manual_vaccination_entry_failed');
      setShowAddForm(false);
      resetAddForm();
      await refetch();
    } catch (err) {
      setErrorText(errorMessage(err));
    }
  };

  const onConfirmVoid = async (): Promise<void> => {
    if (!voiding_entry) return;
    setErrorText(null);
    if (!void_reason.trim()) {
      setErrorText(t('vaccination_card.error_void_reason_required'));
      return;
    }
    try {
      const result = await voidEntry({
        variables: { input: { entry_id: voiding_entry.id, reason: void_reason.trim() } },
        // eslint-disable-next-line @typescript-eslint/naming-convention
        context: { headers: { 'Idempotency-Key': generateIdempotencyKey() } },
      });
      assertMutationSucceeded(result, 'void_vaccination_card_entry_failed');
      setVoidingEntry(null);
      setVoidReason('');
      await refetch();
    } catch (err) {
      setErrorText(errorMessage(err));
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur"
    >
      <Card className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden">
        <CardHeader>
          <div className="flex items-start justify-between gap-2">
            <div>
              <CardTitle>{t('vaccination_card.title', { name: pet_name })}</CardTitle>
              <p className="text-xs text-muted-foreground">
                {card
                  ? t('vaccination_card.card_number', { number: card.card_number })
                  : t('vaccination_card.no_card_yet')}
              </p>
            </div>
            {can_write && !show_add_form ? (
              <Button type="button" size="sm" onClick={() => setShowAddForm(true)}>
                <Plus className="h-4 w-4" aria-hidden />
                <span>{t('vaccination_card.add_entry')}</span>
              </Button>
            ) : null}
          </div>
        </CardHeader>

        <CardContent className="flex-1 space-y-3 overflow-y-auto">
          {error_text ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
              {error_text}
            </div>
          ) : null}

          {show_add_form ? (
            <Card className="border-primary/40">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">{t('vaccination_card.add_entry_title')}</CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label htmlFor="vc-vaccine-name">{t('vaccination_card.field_vaccine_name')}</Label>
                  <Input
                    id="vc-vaccine-name"
                    value={vaccine_name}
                    onChange={(e) => setVaccineName(e.target.value)}
                    disabled={adding}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="vc-applied-date">{t('vaccination_card.field_applied_date')}</Label>
                  <Input
                    id="vc-applied-date"
                    type="date"
                    max={todayIso()}
                    value={applied_date}
                    onChange={(e) => setAppliedDate(e.target.value)}
                    disabled={adding}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="vc-lot-number">{t('vaccination_card.field_lot_number')}</Label>
                  <Input
                    id="vc-lot-number"
                    value={lot_number}
                    onChange={(e) => setLotNumber(e.target.value)}
                    disabled={adding}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="vc-next-application-date">
                    {t('vaccination_card.field_next_application_date')}
                  </Label>
                  <Input
                    id="vc-next-application-date"
                    type="date"
                    min={todayIso()}
                    value={next_application_date}
                    onChange={(e) => setNextApplicationDate(e.target.value)}
                    disabled={adding}
                  />
                </div>
                <div className="space-y-1">
                  <Label htmlFor="vc-weight-kg">{t('vaccination_card.field_weight')}</Label>
                  <Input
                    id="vc-weight-kg"
                    inputMode="decimal"
                    value={weight_kg}
                    onChange={(e) => setWeightKg(e.target.value)}
                    disabled={adding}
                    placeholder={t('vaccination_card.field_weight_placeholder')}
                  />
                </div>
                <div className="space-y-1 sm:col-span-2">
                  <Label htmlFor="vc-notes">{t('vaccination_card.field_notes')}</Label>
                  <textarea
                    id="vc-notes"
                    className="border-input bg-background min-h-[60px] w-full rounded-md border px-3 py-2 text-sm"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={adding}
                  />
                </div>
              </CardContent>
              <CardFooter className="justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  disabled={adding}
                  onClick={() => {
                    setShowAddForm(false);
                    resetAddForm();
                    setErrorText(null);
                  }}
                >
                  {t('common.cancel')}
                </Button>
                <Button
                  type="button"
                  disabled={adding}
                  onClick={() => {
                    void onSubmitAddEntry();
                  }}
                >
                  {adding ? t('common.loading') : t('common.save')}
                </Button>
              </CardFooter>
            </Card>
          ) : null}

          {loading && !card ? (
            <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : null}

          {!loading && entries.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              {t('vaccination_card.empty')}
            </p>
          ) : null}

          {entries.map((entry) => (
            <Card key={entry.id} className={entry.voided_at ? 'opacity-60' : undefined}>
              <CardContent className="space-y-1 p-3 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-medium">{entry.vaccine_name}</span>
                  <div className="flex flex-wrap items-center gap-1">
                    <Badge variant={entry.source === 'sale' ? 'default' : 'secondary'}>
                      {t(`vaccination_card.source_${entry.source}`)}
                    </Badge>
                    {entry.voided_at ? (
                      <Badge variant="destructive">{t('vaccination_card.voided')}</Badge>
                    ) : null}
                  </div>
                </div>
                <div className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                  <span>
                    {t('vaccination_card.field_applied_date')}:{' '}
                    <span className="text-foreground">{entry.applied_date}</span>
                  </span>
                  <span>
                    {t('vaccination_card.field_next_application_date')}:{' '}
                    <span className="text-foreground">
                      {entry.next_application_date ?? t('vaccination_card.field_not_recorded')}
                    </span>
                  </span>
                  <span>
                    {t('vaccination_card.field_lot_number')}:{' '}
                    <span className="font-mono text-foreground">{entry.lot_number}</span>
                  </span>
                  <span>
                    {t('vaccination_card.field_weight')}:{' '}
                    <span className="text-foreground">
                      {entry.weight_grams_snapshot !== null
                        ? `${(entry.weight_grams_snapshot / 1000).toFixed(3)} kg`
                        : t('vaccination_card.field_not_recorded')}
                    </span>
                  </span>
                  <span className="sm:col-span-2">
                    {t('vaccination_card.field_veterinarian')}:{' '}
                    <span className="text-foreground">
                      {entry.veterinarian_name_snapshot}
                      {entry.veterinarian_license_snapshot
                        ? ` (${t('vaccination_card.license_prefix')} ${entry.veterinarian_license_snapshot})`
                        : ''}
                    </span>
                  </span>
                </div>
                {entry.notes ? <p className="text-xs text-muted-foreground">{entry.notes}</p> : null}
                {entry.voided_at ? (
                  <p className="text-xs text-destructive">
                    {t('vaccination_card.void_reason')}: {entry.void_reason}
                  </p>
                ) : is_admin ? (
                  <div className="flex justify-end pt-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-destructive"
                      onClick={() => {
                        setVoidingEntry(entry);
                        setVoidReason('');
                        setErrorText(null);
                      }}
                    >
                      {t('vaccination_card.void_entry')}
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          ))}
        </CardContent>

        <CardFooter className="justify-end border-t pt-3">
          <Button type="button" variant="outline" onClick={on_close}>
            {t('common.close')}
          </Button>
        </CardFooter>
      </Card>

      {voiding_entry ? (
        <div
          role="alertdialog"
          aria-modal="true"
          className="fixed inset-0 z-[60] flex items-center justify-center bg-background/80 p-4 backdrop-blur"
        >
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="text-base">{t('vaccination_card.void_entry_title')}</CardTitle>
              <p className="text-sm text-muted-foreground">
                {t('vaccination_card.void_entry_description', {
                  vaccine: voiding_entry.vaccine_name,
                })}
              </p>
            </CardHeader>
            <CardContent className="space-y-1">
              <Label htmlFor="vc-void-reason">{t('vaccination_card.field_void_reason')}</Label>
              <textarea
                id="vc-void-reason"
                className="border-input bg-background min-h-[60px] w-full rounded-md border px-3 py-2 text-sm"
                value={void_reason}
                onChange={(e) => setVoidReason(e.target.value)}
                disabled={voiding}
              />
            </CardContent>
            <CardFooter className="justify-end gap-2">
              <Button
                type="button"
                variant="ghost"
                disabled={voiding}
                onClick={() => {
                  setVoidingEntry(null);
                  setVoidReason('');
                }}
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={voiding}
                onClick={() => {
                  void onConfirmVoid();
                }}
              >
                {voiding ? t('common.loading') : t('vaccination_card.void_entry')}
              </Button>
            </CardFooter>
          </Card>
        </div>
      ) : null}
    </div>
  );
}
